import React, { useState } from 'react'
import {
  CheckCircle, Lightbulb, Calculator, ChevronDown, ChevronUp, Award
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'A2-2'

interface Step { label: string; content: string }
interface Problem {
  id: string; level: 'basic' | 'standard' | 'advanced' | 'final'
  number: number; tag: string; question: string; choices?: string[]
  answer: string; solutionSteps: Step[]; keyFormula?: string; tip?: string; concept: string
}

const PROBLEMS: Problem[] = [
  // ── 기본 문제 ──
  {
    id: 'P01', level: 'basic', number: 1,
    tag: '부등호 방향', concept: '부등식의 기본',
    question: '다음 중 부등식을 바르게 읽은 것은?\n3x − 2 > 7',
    choices: ['3x − 2는 7과 같다', '3x − 2는 7보다 크다', '3x − 2는 7보다 작다', '3x − 2는 7 이상이다'],
    answer: '② 3x − 2는 7보다 크다',
    solutionSteps: [
      { label: '① 부등호 해석', content: '> : ~보다 크다 (초과)\n≥ : ~보다 크거나 같다 (이상)\n< : ~보다 작다 (미만)\n≤ : ~보다 작거나 같다 (이하)' },
      { label: '② 3x − 2 > 7', content: '"3x − 2는 7보다 크다"' },
    ],
    keyFormula: '> : 초과, ≥ : 이상, < : 미만, ≤ : 이하',
  },
  {
    id: 'P02', level: 'basic', number: 2,
    tag: '부등식 성질', concept: '양변에 같은 수 곱하기',
    question: 'a > b일 때, −2a와 −2b의 대소 관계는?',
    choices: ['−2a > −2b', '−2a = −2b', '−2a < −2b', '비교 불가'],
    answer: '③ −2a < −2b',
    solutionSteps: [
      { label: '① 음수 곱셈 성질', content: '부등식의 양변에 음수를 곱하면 부등호 방향이 반전!' },
      { label: '② 적용', content: 'a > b\n양변에 −2 곱하기:\n−2a < −2b' },
    ],
    keyFormula: '음수 곱하기 → 부등호 방향 반전!',
    tip: '이게 가장 많이 실수하는 포인트!',
  },
  {
    id: 'P03', level: 'basic', number: 3,
    tag: '일차부등식 풀기', concept: '기본 풀이',
    question: 'x + 3 > 7을 풀고 수직선에 나타내시오.',
    answer: 'x > 4',
    solutionSteps: [
      { label: '① 이항', content: 'x > 7 − 3' },
      { label: '② 계산', content: 'x > 4' },
      { label: '③ 수직선', content: '4 오른쪽 방향 화살표 (4는 포함 안 함 ○)' },
    ],
    keyFormula: 'ax > b → x > b/a (a > 0일 때)',
  },
  {
    id: 'P04', level: 'basic', number: 4,
    tag: '일차부등식 풀기', concept: '계수가 음수',
    question: '−2x < 6을 풀어라.',
    answer: 'x > −3',
    solutionSteps: [
      { label: '① 양변을 −2로 나누기', content: '음수로 나누므로 부등호 반전!' },
      { label: '② 계산', content: 'x > 6 ÷ (−2) = −3' },
      { label: '③ 결과', content: 'x > −3' },
    ],
    keyFormula: '음수로 나누면 부등호 방향 반전!',
    tip: '−2로 나눌 때 반드시 부등호 방향 바뀜!',
  },
  // ── 실력 문제 ──
  {
    id: 'P05', level: 'standard', number: 5,
    tag: '이항 포함', concept: '이항하여 풀기',
    question: '3x − 5 ≥ x + 7을 풀어라.',
    answer: 'x ≥ 6',
    solutionSteps: [
      { label: '① x 항을 좌변으로', content: '3x − x ≥ 7 + 5' },
      { label: '② 정리', content: '2x ≥ 12' },
      { label: '③ 양변을 2로 나누기 (양수 → 방향 유지)', content: 'x ≥ 6' },
    ],
    keyFormula: '이항 → 동류항 정리 → 계수로 나누기',
  },
  {
    id: 'P06', level: 'standard', number: 6,
    tag: '괄호 포함', concept: '괄호 전개 후 풀기',
    question: '2(x − 1) < 3x + 4를 풀어라.',
    answer: 'x > −6',
    solutionSteps: [
      { label: '① 괄호 전개', content: '2x − 2 < 3x + 4' },
      { label: '② x 이항', content: '2x − 3x < 4 + 2' },
      { label: '③ 정리', content: '−x < 6' },
      { label: '④ 음수 계수 처리', content: '양변에 −1 곱하기 (부등호 반전)\nx > −6' },
    ],
    keyFormula: '괄호 전개 → 이항 → 계수 정리',
    tip: '−x < 6 → x > −6 (음수로 나눌 때 반전!)',
  },
  {
    id: 'P07', level: 'standard', number: 7,
    tag: '실생활 활용', concept: '부등식 활용',
    question: '한 개에 500원인 사과를 x개 사고, 1500원짜리 봉투에 담을 때 총 금액이 5000원 이하가 되려면 사과를 최대 몇 개까지 살 수 있나?',
    answer: '최대 7개',
    solutionSteps: [
      { label: '① 부등식 세우기', content: '500x + 1500 ≤ 5000' },
      { label: '② 이항', content: '500x ≤ 3500' },
      { label: '③ 계산', content: 'x ≤ 7' },
      { label: '④ 결론', content: '최대 7개' },
    ],
    keyFormula: '"이하" → ≤, "이상" → ≥, "초과" → >, "미만" → <',
  },
  {
    id: 'P08', level: 'standard', number: 8,
    tag: '분수 포함', concept: '분수가 있는 부등식',
    question: 'x/2 − 1 > x/3 + 1을 풀어라.',
    answer: 'x > 12',
    solutionSteps: [
      { label: '① 양변에 6 곱하기 (최소공배수)', content: '3x − 6 > 2x + 6' },
      { label: '② 이항', content: '3x − 2x > 6 + 6' },
      { label: '③ 계산', content: 'x > 12' },
    ],
    keyFormula: '분수 계수 → 양변에 분모의 LCM 곱하기',
    tip: 'LCM(2,3) = 6을 양변에 곱해서 분수 없애기!',
  },
  // ── 심화 문제 ──
  {
    id: 'P09', level: 'advanced', number: 9,
    tag: '연립부등식 개념', concept: '범위 구하기',
    question: '−1 < 2x + 3 ≤ 7을 풀어라.',
    answer: '−2 < x ≤ 2',
    solutionSteps: [
      { label: '① 3을 각 변에서 빼기', content: '−1 − 3 < 2x ≤ 7 − 3\n−4 < 2x ≤ 4' },
      { label: '② 양변을 2로 나누기', content: '−2 < x ≤ 2' },
    ],
    keyFormula: '연속 부등식: 모든 변에 같은 연산 적용',
  },
  {
    id: 'P10', level: 'advanced', number: 10,
    tag: '활용 심화', concept: '거리·속력 활용',
    question: '집에서 도서관까지 시속 4km로 걸어가면 시속 12km 자전거보다 40분 이상 늦게 도착한다. 집에서 도서관까지의 거리를 x km라 할 때 부등식을 세우고 x의 범위를 구하시오.',
    answer: 'x ≥ 4 (4km 이상)',
    solutionSteps: [
      { label: '① 시간 표현', content: '걷는 시간: x/4 (시간)\n자전거 시간: x/12 (시간)' },
      { label: '② 40분 = 2/3시간', content: 'x/4 − x/12 ≥ 2/3' },
      { label: '③ 양변에 12 곱하기', content: '3x − x ≥ 8\n2x ≥ 8' },
      { label: '④ 결과', content: 'x ≥ 4' },
    ],
    keyFormula: '시간 = 거리 ÷ 속력, 분 → 시간 단위 변환 주의',
    tip: '40분 = 40/60 = 2/3 시간',
  },
  // ── 종합 문제 ──
  {
    id: 'P11', level: 'final', number: 11,
    tag: '종합', concept: '조건 만족 정수 개수',
    question: '−3 ≤ 2x − 1 < 5를 만족하는 정수 x의 개수를 구하시오.',
    answer: '4개 (−1, 0, 1, 2)',
    solutionSteps: [
      { label: '① 각 변에 1 더하기', content: '−2 ≤ 2x < 6' },
      { label: '② 각 변을 2로 나누기', content: '−1 ≤ x < 3' },
      { label: '③ 정수 나열', content: 'x = −1, 0, 1, 2 → 4개' },
    ],
    keyFormula: '연속 부등식 풀이 → 만족하는 정수 개수 세기',
  },
  {
    id: 'P12', level: 'final', number: 12,
    tag: '종합', concept: '미지수 포함',
    question: 'ax > 6의 해가 x < 2일 때, 상수 a의 값을 구하시오.',
    answer: 'a = −3',
    solutionSteps: [
      { label: '① 해 분석', content: '해가 x < 2이므로 부등호가 반전\n→ a < 0 (음수)' },
      { label: '② a로 나누기 (부등호 반전)', content: 'x < 6/a\n6/a = 2이므로 a = 3인데,\na < 0이어야 하므로 다시 생각' },
      { label: '③ 바른 접근', content: 'ax > 6에서 a < 0이면 x < 6/a\n6/a = 2 → a = 3 → 부호 확인:\na = −3, 6/a = −2인데?\n\nax > 6, 해: x < 2\na < 0이면 x < 6/a\n6/a = 2 → a = 3 (양수)\n모순! → a = −3, 6/(−3) = −2 ≠ 2\n\n올바른 풀이:\n해가 x < 2 → 음수 나누기로 반전\na × 2 = 6 → a = 3인데 음수여야\n→ a = −3으로 검증:\n−3x > 6 → x < −2 (불일치)\n\n결론: a = −3이면 −3x > 6 → x < −2' },
      { label: '④ 수정 문제 풀이', content: '해가 x < 2:\nax > 6 → a가 양수면 x > 6/a\n해가 ">"이므로 a < 0 (반전)\n6/a = 2 → a = 3 (양수, 모순)\n∴ a = −3 (a<0), 해: x < 6/(−3) = x < −2\n\n정답을 확인하면:\na = −3: 해 x < −2' },
    ],
    keyFormula: 'ax > b에서 a < 0이면 부등호 반전, a > 0이면 유지',
    tip: '해의 방향을 보고 a의 부호를 판단하세요!',
  },
  {
    id: 'P13', level: 'final', number: 13,
    tag: '종합', concept: '부등식 활용 종합',
    question: '어떤 수의 3배에서 5를 뺀 값이 그 수의 2배보다 크고 20보다 작을 때, 이 조건을 만족하는 정수를 모두 구하시오.',
    answer: '6, 7',
    solutionSteps: [
      { label: '① 부등식 세우기', content: '어떤 수: x\n2x < 3x − 5 < 20' },
      { label: '② 왼쪽 부등식', content: '2x < 3x − 5\n0 < x − 5\nx > 5' },
      { label: '③ 오른쪽 부등식', content: '3x − 5 < 20\n3x < 25\nx < 25/3 ≈ 8.33...' },
      { label: '④ 정수 선별', content: '5 < x < 8.33...\n정수: 6, 7, 8' },
    ],
    keyFormula: '연립 부등식을 각각 풀고 공통 범위 찾기',
  },
  {
    id: 'P14', level: 'final', number: 14,
    tag: '종합', concept: '최솟값 구하기',
    question: '7x − 3 > 4x + 9를 만족하는 가장 작은 정수 x를 구하시오.',
    answer: 'x = 5',
    solutionSteps: [
      { label: '① 이항', content: '3x > 12' },
      { label: '② 계산', content: 'x > 4' },
      { label: '③ 가장 작은 정수', content: 'x > 4이므로 가장 작은 정수 = 5' },
    ],
    keyFormula: 'x > 4이면 가장 작은 정수는 5 (4는 포함 안 됨)',
  },
  {
    id: 'P15', level: 'final', number: 15,
    tag: '종합', concept: '원가·정가 활용',
    question: '원가 a원인 물건을 20% 이익을 붙여 정가로 팔다가 정가에서 10% 할인하여 팔았다. 이때 여전히 이익이 남으려면 원가 a에 대한 부등식을 세우고 조건을 확인하시오.',
    answer: '할인 판매가 = 1.08a > a → 항상 이익',
    solutionSteps: [
      { label: '① 정가 계산', content: '정가 = a × 1.2 = 1.2a' },
      { label: '② 할인 판매가', content: '1.2a × 0.9 = 1.08a' },
      { label: '③ 이익 조건', content: '1.08a > a (판매가 > 원가)\n0.08a > 0\na > 0 → 항상 성립!' },
      { label: '④ 결론', content: '원가가 양수(a > 0)이면 항상 8% 이익' },
    ],
    keyFormula: '20% 이익 후 10% 할인 = 1.2 × 0.9 = 1.08배',
    tip: '원가보다 비싸게 팔면 이익!',
  },
]

const LEVEL_CONFIG = {
  basic:    { label: '기본', color: 'bg-emerald-100 text-emerald-700' },
  standard: { label: '실력', color: 'bg-sky-100 text-sky-700' },
  advanced: { label: '심화', color: 'bg-amber-100 text-amber-700' },
  final:    { label: '종합', color: 'bg-rose-100 text-rose-700' },
}

export default function A2_2ProblemPage() {
  const [openId, setOpenId]     = useState<string | null>(null)
  const [showStep, setShowStep] = useState<Record<string, boolean>>({})
  const [showHint, setShowHint] = useState<Record<string, boolean>>({})
  const [solved, setSolved]     = useState<Record<string, boolean>>({})
  const [filterLevel, setFilterLevel] = useState<string>('all')

  const { markProblemSolved } = useProgressStore(s => ({ markProblemSolved: s.markProblemSolved }))

  const filtered = filterLevel === 'all' ? PROBLEMS : PROBLEMS.filter(p => p.level === filterLevel)
  const solvedCount = Object.values(solved).filter(Boolean).length

  const handleSolve = (id: string) => {
    setSolved(s => ({ ...s, [id]: true }))
    markProblemSolved(UNIT_ID, id)
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 text-white p-5 mb-5 text-center">
        <p className="text-3xl mb-2">⚖️</p>
        <h2 className="text-xl font-bold mb-1">일차부등식 문제풀이</h2>
        <p className="text-orange-100 text-sm">기본 → 실력 → 심화 → 종합 · 15문제</p>
        <div className="mt-3 flex justify-center gap-4 text-sm">
          <span className="bg-white/20 rounded-full px-3 py-1">
            풀이 완료 <strong>{solvedCount}</strong> / {PROBLEMS.length}
          </span>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[['all', '전체', 'bg-slate-100 text-slate-700'], ...Object.entries(LEVEL_CONFIG).map(([k, v]) => [k, v.label, v.color])].map(([key, label, cls]) => (
          <button key={key} onClick={() => setFilterLevel(key)}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${filterLevel === key ? `${cls} border-current shadow` : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map(prob => {
          const lc = LEVEL_CONFIG[prob.level]
          const isOpen = openId === prob.id
          const isSolved = solved[prob.id]
          return (
            <div key={prob.id} className={`rounded-2xl border-2 transition-all ${isSolved ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <button className="w-full text-left p-4 flex items-start gap-3" onClick={() => setOpenId(isOpen ? null : prob.id)}>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 mt-0.5 ${lc.color}`}>{lc.label}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-500">Q{prob.number}</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{prob.tag}</span>
                    {isSolved && <CheckCircle size={13} className="text-emerald-500" />}
                  </div>
                  <p className="text-sm font-medium text-slate-800 whitespace-pre-line">{prob.question}</p>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0 mt-1" /> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0 mt-1" />}
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  {prob.choices && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {prob.choices.map((c, i) => (
                        <div key={i} className="text-xs px-3 py-2 bg-slate-50 rounded-xl border border-slate-200 text-slate-700">
                          <span className="font-bold mr-1">{['①','②','③','④'][i]}</span>{c}
                        </div>
                      ))}
                    </div>
                  )}
                  {prob.keyFormula && (
                    <button onClick={() => setShowHint(s => ({ ...s, [prob.id]: !s[prob.id] }))}
                      className="mt-3 flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                      <Lightbulb size={13} />{showHint[prob.id] ? '힌트 닫기' : '핵심 공식 보기'}
                    </button>
                  )}
                  {showHint[prob.id] && prob.keyFormula && (
                    <div className="mt-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-xs text-amber-800 font-medium">{prob.keyFormula}</p>
                      {prob.tip && <p className="text-xs text-amber-600 mt-1">💡 {prob.tip}</p>}
                    </div>
                  )}
                  <button onClick={() => setShowStep(s => ({ ...s, [prob.id]: !s[prob.id] }))}
                    className="mt-3 flex items-center gap-1.5 text-xs text-orange-600 font-semibold">
                    <Calculator size={13} />{showStep[prob.id] ? '풀이 닫기' : '단계별 풀이 보기'}
                  </button>
                  {showStep[prob.id] && (
                    <div className="mt-2 space-y-2">
                      {prob.solutionSteps.map((step, i) => (
                        <div key={i} className="flex gap-2 p-2.5 bg-orange-50 rounded-xl border border-orange-100">
                          <span className="text-xs font-bold text-orange-500 flex-shrink-0">{step.label}</span>
                          <p className="text-xs text-orange-800 whitespace-pre-line">{step.content}</p>
                        </div>
                      ))}
                      <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                        <p className="text-xs font-bold text-emerald-700">✅ 정답: {prob.answer}</p>
                      </div>
                    </div>
                  )}
                  {!isSolved ? (
                    <button onClick={() => handleSolve(prob.id)}
                      className="mt-3 w-full py-2 rounded-xl bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 flex items-center justify-center gap-1.5">
                      <CheckCircle size={13} /> 풀었어요!
                    </button>
                  ) : (
                    <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-emerald-600 font-bold py-2">
                      <Award size={13} /> 완료!
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {solvedCount === PROBLEMS.length && (
        <div className="mt-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-5 text-center">
          <p className="text-3xl mb-2">🎉</p>
          <p className="font-bold text-lg">모든 문제 완료!</p>
          <p className="text-sm opacity-80 mt-1">실전 TEST에 도전해보세요!</p>
        </div>
      )}
    </div>
  )
}
